import { useEffect, useRef, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "../components/Layout";
import SortableGrid from "../components/SortableGrid";
import api, { pollO365WithProgress } from "../lib/api";
import { fmtCur, fmtNum, fmtDate } from "../lib/format";
import { PushToErpDialog } from "../components/PushToErpDialog";
import { CustomerSelect } from "../components/CustomerSelect";
import { ItemSelect } from "../components/ItemSelect";

// Earliest line delivery date — header requested_ship_date is often blank.
function reqShip(p) {
  if (p.requested_ship_date) return p.requested_ship_date;
  const ds = (p.line_items || []).map((l) => l.due_date).filter(Boolean).sort();
  return ds[0] || null;
}
const qtyOrdered = (p) => (p.line_items || []).reduce((s, l) => s + (Number(l.quantity_ordered) || 0), 0);
// PO total: use the extracted total_amount when present, else derive it from the line
// items (qty × unit_price) so the grid never shows a blank amount for a PO that clearly
// has priced lines (e.g. LG Sourcing POs where total_amount wasn't captured).
const amtTotal = (p) => {
  const t = Number(p.total_amount);
  if (t) return t;
  return (p.line_items || []).reduce((s, l) => s + (Number(l.quantity_ordered) || 0) * (Number(l.unit_price) || 0), 0);
};

export default function IntakeReview() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null); // PO open in the review modal
  const [saving, setSaving] = useState(false);
  const [polling, setPolling] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [pushPo, setPushPo] = useState(null); // PO to push to ERP after confirm
  const [enriching, setEnriching] = useState(false);
  const [view, setView] = useState("review"); // review | pushed
  const [correctPo, setCorrectPo] = useState(null); // pushed PO to re-route to correct customer/plant
  const [importingPo2, setImportingPo2] = useState(false);
  const [pushingAll, setPushingAll] = useState(false);
  const [writingErp, setWritingErp] = useState(false);
  const [reparsing, setReparsing] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const loadSeq = useRef(0);
  const dcFileRef = useRef(null);
  const po2FileRef = useRef(null);
  const legrandErpFileRef = useRef(null);
  // Price Check tab: last-booked price vs the new PO's price, to catch pricing drift at intake.
  const [priceData, setPriceData] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceExpanded, setPriceExpanded] = useState({});

  // Price History tab: invoice-based price timeline per item+customer (for all customers/SKUs).
  const [histData, setHistData] = useState(null);
  const [histLoading, setHistLoading] = useState(false);
  const [histQ, setHistQ] = useState("");
  const [histCustomer, setHistCustomer] = useState("");
  const [histPage, setHistPage] = useState(1);
  const [histExpanded, setHistExpanded] = useState({});

  const loadHistory = (page = histPage) => {
    setHistLoading(true);
    api.get("/price-history", { params: { q: histQ || undefined, customer: histCustomer || undefined, page, page_size: 50 } })
      .then((r) => { setHistData(r.data); setHistPage(r.data?.page || 1); })
      .catch(() => toast.error("Failed to load price history"))
      .finally(() => setHistLoading(false));
  };

  const [histDownloading, setHistDownloading] = useState(false);
  const downloadHistoryCsv = async () => {
    setHistDownloading(true);
    try {
      let page = 1, pages = 1, all = [];
      do {
        const { data } = await api.get("/price-history", { params: { q: histQ || undefined, customer: histCustomer || undefined, page, page_size: 200 } });
        all = all.concat(data.rows || []);
        pages = data.pages || 1; page += 1;
      } while (page <= pages && page <= 30);
      const esc = (v) => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
      const header = ["Item", "Description", "Customer", "Currency", "Effective Date", "PO #", "Total Qty", "Price", "Previous Price"];
      const lines = [header.join(",")];
      for (const r of all)
        for (const c of (r.changes || []))
          lines.push([r.item, r.name, r.customer, r.currency, c.date, c.pono, c.qty, c.price, c.from_price == null ? "" : c.from_price].map(esc).join(","));
      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `price-history${histCustomer ? "-" + histCustomer.replace(/[^a-z0-9]+/gi, "_") : ""}${histQ ? "-" + histQ.replace(/[^a-z0-9]+/gi, "_") : ""}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${all.length} item series (${lines.length - 1} price rows)`);
    } catch (e) {
      toast.error("Download failed");
    } finally { setHistDownloading(false); }
  };

  const loadPriceCheck = () => {
    setPriceLoading(true);
    api.get("/po/price-check", { params: { status: "pending_review" } })
      .then((r) => setPriceData(r.data))
      .catch(() => toast.error("Failed to load price check"))
      .finally(() => setPriceLoading(false));
  };

  const load = () => {
    if (view === "price") { loadPriceCheck(); return; }
    if (view === "history") { if (!histData) loadHistory(1); return; }
    // Clear stale rows immediately so the previous tab's list can't linger, and tag this request
    // so a slow/out-of-order response from a tab you already switched away from is ignored.
    const seq = ++loadSeq.current;
    setRows([]);
    setLoading(true);
    const params = view === "pushed"
      ? { status: "pushed_to_erp", include_superseded: true }
      : view === "confirmed"
      ? { status: "confirmed" }
      : { status: "pending_review" };
    api.get("/po", { params })
      .then((r) => { if (seq === loadSeq.current) setRows(r.data || []); })
      .catch(() => { if (seq === loadSeq.current) toast.error("Failed to load POs"); })
      .finally(() => { if (seq === loadSeq.current) setLoading(false); });
  };
  useEffect(load, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  const pollO365 = async () => {
    setPolling(true);
    try {
      const d = await pollO365WithProgress();
      toast.success(`O365: fetched ${d.fetched ?? 0}, ingested ${d.processed ?? 0}, skipped ${d.skipped_already_ingested ?? 0}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "O365 polling failed");
    } finally {
      setPolling(false);
    }
  };

  // Reconcile "pushed" status: some Confirmed POs are ALREADY booked in the live ERP (pushed from
  // another environment or before the status flip). Match each Confirmed PO's Customer PO against
  // dbo.po and flip the ones already in the ERP to pushed_to_erp so they leave "Confirmed (to push)"
  // and move to "Pushed POs". Dry-run first, then apply on confirm. Reversible & idempotent.
  const reconcilePushed = async () => {
    setReconciling(true);
    try {
      const dry = await api.post("/admin/intake/reconcile-pushed-status?apply=false");
      const n = dry.data?.matched || 0;
      if (!n) {
        toast.info(`No intake POs are already in the ERP — nothing to clear.${dry.data?.ambiguous ? ` (${dry.data.ambiguous} ambiguous, skipped)` : ""}`);
        return;
      }
      if (!window.confirm(
        `${n} intake PO(s) are ALREADY booked in the live ERP.\n\n` +
        `Move them to "Pushed POs" (flip status to pushed_to_erp) so they leave this list?` +
        `${dry.data?.ambiguous ? `\n\n${dry.data.ambiguous} PO(s) map to multiple ERP orders and will be skipped.` : ""}`
      )) return;
      const { data } = await api.post("/admin/intake/reconcile-pushed-status?apply=true");
      toast.success(`Reconciled ${data.reconciled} already-in-ERP PO(s) → moved to Pushed POs.${data.ambiguous ? ` ${data.ambiguous} skipped (ambiguous).` : ""}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reconcile pushed status");
    } finally {
      setReconciling(false);
    }
  };

  const recoverSources = async () => {
    setRecovering(true);
    try {
      const r = await api.post("/po/refetch-sources");
      const { missing, emails_refetched, linked } = r.data;
      if (!missing) toast.success("All POs already have their source document on file");
      else toast.success(`Recovered ${linked} source document(s) from ${emails_refetched} email(s) · ${missing} PO(s) were missing`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not re-fetch missing sources");
    } finally {
      setRecovering(false);
    }
  };

  // Enrich Walmart intake POs with real items from a DC Distribution Excel. Matches
  // by PO# (leading zeros stripped) and replaces the generic "consol" lines with the
  // true SKU/description/qty/price + resolved ERP VSN so the PO is ERP-push ready.
  const enrichFromDc = async (fileInputEvent) => {
    const file = fileInputEvent.target.files?.[0];
    fileInputEvent.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    setEnriching(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("full_fix", "true");
      const { data } = await api.post("/intake/enrich-dc-distribution", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(
        `DC enrichment: matched ${data.matched_pos}/${data.excel_po_count} PO(s), updated ${data.updated_pos} copy(ies), ${data.updated_lines} line(s).`
      );
      if ((data.unmatched_po_numbers || []).length)
        toast.warning(`${data.unmatched_po_numbers.length} Excel PO(s) had no intake match: ${data.unmatched_po_numbers.slice(0, 8).join(", ")}${data.unmatched_po_numbers.length > 8 ? "…" : ""}`);
      if ((data.missing_vsn_items || []).length)
        toast.warning(`No ERP VSN found for ${data.missing_vsn_items.length} item(s): ${data.missing_vsn_items.slice(0, 8).join(", ")}${data.missing_vsn_items.length > 8 ? "…" : ""}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not enrich POs from the DC Excel");
    } finally {
      setEnriching(false);
    }
  };

  // Bulk-set the End-Customer PO Number (cust_po2) on Legrand intake POs from a
  // 2-column Excel (Col A = our PO#, Col B = End-Customer PO Number).
  const importCustPo2 = async (fileInputEvent) => {
    const file = fileInputEvent.target.files?.[0];
    fileInputEvent.target.value = "";
    if (!file) return;
    setImportingPo2(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/intake/import-cust-po2", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`End-Customer PO import: matched ${data.matched_pos}/${data.excel_row_count} PO(s), updated ${data.updated_pos}.`);
      if ((data.unmatched_po_numbers || []).length)
        toast.warning(`${data.unmatched_po_numbers.length} row(s) had no Legrand intake match: ${data.unmatched_po_numbers.slice(0, 8).join(", ")}${data.unmatched_po_numbers.length > 8 ? "…" : ""}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not import End-Customer PO numbers");
    } finally {
      setImportingPo2(false);
    }
  };

  // Push every confirmed intake PO to the ERP in one action. Runs a dry-run preview
  // first, then asks for confirmation before writing.
  const pushAllConfirmed = async () => {
    setPushingAll(true);
    try {
      const dry = await api.post("/intake/push-all-confirmed", { confirm: false });
      const d = dry.data;
      const ok = window.confirm(
        `Push all confirmed POs to the ERP?\n\n` +
        `Ready to push: ${d.pushed_count}\n` +
        `Blocked (need manual fix): ${d.blocked_count}\n` +
        `Errors: ${d.error_count}\n\n` +
        `Only the ${d.pushed_count} ready PO(s) will be written. Continue?`
      );
      if (!ok) { setPushingAll(false); return; }
      const { data } = await api.post("/intake/push-all-confirmed", { confirm: true });
      toast.success(`Pushed ${data.pushed_count} PO(s) to ERP.`);
      if (data.blocked_count)
        toast.warning(`${data.blocked_count} PO(s) blocked — push them individually to resolve.`);
      if (data.error_count)
        toast.error(`${data.error_count} PO(s) errored during push.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk push failed");
    } finally {
      setPushingAll(false);
    }
  };

  // Re-derive ordered EACHES from each Walmart Import PO's source (fixes POs that booked the
  // carton count). Dry-run preview first, then apply. Already-pushed POs are audited, not changed.
  const reparseWalmartQty = async () => {
    setReparsing(true);
    try {
      const dry = await api.post("/intake/reparse-walmart-qty", { confirm: false });
      const d = dry.data;
      const auditNote = d.erp_audit_count
        ? `\n\n⚠ ${d.erp_audit_count} PO(s) ALREADY PUSHED to the ERP have the wrong (carton) qty. ` +
          `These are NOT changed here — correct them in the ERP:\n` +
          d.erp_audit.map((a) => `  • ${a.po_number}: ${a.changes.map((c) => `${c.old_qty}→${c.new_qty}`).join(", ")}`).join("\n")
        : "";
      if (!d.fixable_count) {
        window.alert(`No pending Walmart Import POs need a quantity correction (scanned ${d.scanned}).${auditNote}`);
        setReparsing(false);
        return;
      }
      const preview = d.fixable.slice(0, 10)
        .map((f) => {
          const q = (f.changes || []).map((c) => `${c.old_qty}→${c.new_qty} EA`).join(", ");
          const parts = [q, f.ship_to_filled ? "ship-to restored" : ""].filter(Boolean).join(" · ");
          return `  • ${f.po_number}: ${parts}`;
        }).join("\n");
      const ok = window.confirm(
        `Re-parse ordered quantities (eaches) from source for ${d.fixable_count} pending Walmart Import PO(s)?\n\n` +
        `${preview}${d.fixable_count > 10 ? `\n  …and ${d.fixable_count - 10} more` : ""}${auditNote}\n\n` +
        `Apply the ${d.fixable_count} correction(s)?`
      );
      if (!ok) { setReparsing(false); return; }
      const { data } = await api.post("/intake/reparse-walmart-qty", { confirm: true });
      toast.success(`Corrected quantities on ${data.fixed_count} PO(s) from source.`);
      if (data.erp_audit_count)
        toast.warning(`${data.erp_audit_count} already-pushed PO(s) still wrong in the ERP — fix them in the ERP.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Re-parse failed");
    } finally {
      setReparsing(false);
    }
  };

  // Write the End-Customer PO into CustPO2 across the LIVE ERP (dbo.po/gst/po_dispatch)
  // for OPEN Legrand POs, from the same 2-column Excel. Dry-run preview → confirm.
  const writeLegrandEndcustErp = async (fileInputEvent) => {
    const file = fileInputEvent.target.files?.[0];
    fileInputEvent.target.value = "";
    if (!file) return;
    setWritingErp(true);
    try {
      const preview = new FormData();
      preview.append("file", file);
      preview.append("confirm", "false");
      const { data: dry } = await api.post("/legrand/write-endcust-po", preview, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const ok = window.confirm(
        `Write End-Customer PO to the LIVE ERP (Legrand, OPEN POs only)?\n\n` +
        `Open Legrand POs: ${dry.open_legrand_pos}\n` +
        `Matched in file (with an end-customer PO): ${dry.in_file_with_endcust}\n` +
        `dbo.po rows to fill: ${dry.po_updates}\n` +
        `Invoices (gst) to fill: ${dry.gst_updates}\n` +
        `Dispatch vouchers to fill: ${dry.dispatch_updates}\n\n` +
        `CustPO is never changed. Only empty CustPO2 cells are filled. Continue?`
      );
      if (!ok) { setWritingErp(false); return; }
      const commit = new FormData();
      commit.append("file", file);
      commit.append("confirm", "true");
      const { data } = await api.post("/legrand/write-endcust-po", commit, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`ERP updated · dbo.po ${data.po_updates}, gst ${data.gst_updates}, dispatch ${data.dispatch_updates} · app tables ${data.mongo_updated}.`);
      if ((data.dispatch_skipped_multi || []).length)
        toast.warning(`${data.dispatch_skipped_multi.length} multi-PO dispatch voucher(s) skipped (ambiguous end-customer PO).`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not write End-Customer PO to the ERP");
    } finally {
      setWritingErp(false);
    }
  };


  // Persist the full edited PO form (all header fields + line items) before confirm/push.
  // Backend diff skips unchanged fields and audits the edits.
  const persistForm = async (poId, payload) => {
    if (!payload) return;
    await api.put(`/po/${poId}`, payload);
  };

  const confirm = async (po, payload) => {
    setSaving(true);
    try {
      await persistForm(po.id, payload);
      await api.post(`/po/${po.id}/confirm`);
      toast.success(`PO ${po.po_number} confirmed — now visible in Open Orders & POs`);
      setActive(null);
      setRows((rs) => rs.filter((r) => r.id !== po.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not confirm this PO");
    } finally {
      setSaving(false);
    }
  };

  // Confirm the PO, then immediately open the "Push to ERP" preview dialog.
  const confirmAndPush = async (po, payload) => {
    setSaving(true);
    try {
      await persistForm(po.id, payload);
      await api.post(`/po/${po.id}/confirm`);
      toast.success(`PO ${po.po_number} confirmed — validating for ERP push…`);
      setActive(null);
      setRows((rs) => rs.filter((r) => r.id !== po.id));
      setPushPo({ id: po.id, po_number: po.po_number });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not confirm this PO");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (po) => {
    const reason = window.prompt(`Reject & delete PO ${po.po_number || ""} (${po.customer_name || "unknown"})?\n\nEnter a reason — it is recorded in the audit log. This cannot be undone.`);
    if (reason === null) return; // cancelled
    setSaving(true);
    try {
      await api.delete(`/po/${po.id}`, { params: { reason: reason || undefined } });
      toast.success(`PO ${po.po_number || ""} rejected`);
      setActive(null);
      setRows((rs) => rs.filter((r) => r.id !== po.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete this PO");
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { key: "po_number", header: "PO #",
      render: (p) => (
        <span>
          <strong className="text-mono" style={{ color: "var(--omh-primary)" }}>{p.po_number || "—"}</strong>
          {p.po_revision && (
            <span className="jde-badge" data-testid={`intake-rev-${p.id}`}
                  style={{ marginLeft: 6, background: "#EDE9FE", color: "#5B21B6", borderColor: "#DDD6FE", fontSize: 9 }}
                  title={`PO revision ${p.po_revision}`}>
              REV {p.po_revision}
            </span>
          )}
          {p.has_change_after_push && (
            <span className="jde-badge" data-testid={`intake-change-after-push-${p.id}`}
                  style={{ marginLeft: 6, background: "#FEF3C7", color: "#92400E", borderColor: "#FDE68A", fontSize: 9 }}
                  title={"Revised AFTER ERP push"
                    + (p.post_push_revision ? ` (REV ${p.post_push_revision})` : "")
                    + ":\n" + (p.post_push_revision_diff || []).map(
                        (d) => `• ${d.field}: ${d.old} → ${d.new}`).join("\n")}>
              ⚠ Revised after push
            </span>
          )}
          {(p.older_versions || []).length > 0 && (
            <span className="jde-badge" data-testid={`intake-older-version-${p.id}`}
                  style={{ marginLeft: 6, background: "#FFEDD5", color: "#9A3412", borderColor: "#FDBA74", fontSize: 9 }}
                  title={"An older version of this Walmart PO exists — use \"Push & overwrite older\" to supersede it:\n"
                    + p.older_versions.map((o) => `• ${o.source} #${o.ms_pono ?? "—"} · ${o.po_date || "no date"} · open ${o.qty_open}`).join("\n")}>
              ⚠ Older version exists
            </span>
          )}
        </span>
      ) },
    { key: "customer_name", header: "Customer",
      render: (p) => (
        <span>
          {p.customer_name || "—"}
          {p.customer_matched === false && (
            <span className="jde-badge" data-testid={`intake-unmatched-${p.id}`}
                  style={{ marginLeft: 6, background: "#FEF3C7", color: "#92400E", borderColor: "#FDE68A", fontSize: 9 }}
                  title="Customer could not be matched to your customer list — assign it in review">
              Unmatched
            </span>
          )}
        </span>
      ) },
    { key: "order_date", header: "PO Date", type: "date", accessor: (p) => p.order_date,
      render: (p) => <span className="text-mono">{fmtDate(p.order_date) || "—"}</span> },
    { key: "req_ship", header: "Req. Ship", accessor: (p) => reqShip(p),
      render: (p) => <span className="text-mono">{fmtDate(reqShip(p)) || <span className="muted">—</span>}</span> },
    { key: "lines", header: "Lines", align: "right", accessor: (p) => (p.line_items || []).length,
      render: (p) => <span className="text-mono">{(p.line_items || []).length}</span> },
    { key: "qty", header: "Qty", align: "right", accessor: (p) => qtyOrdered(p),
      render: (p) => <span className="text-mono">{fmtNum(qtyOrdered(p))}</span> },
    { key: "amount", header: "Amount", align: "right", accessor: (p) => amtTotal(p),
      render: (p) => <span className="text-mono">{fmtCur(amtTotal(p), p.currency || "USD")}</span> },
    { key: "source_type", header: "Source",
      render: (p) => (
        <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
          <span className="jde-badge">{(p.source_type || "").replace("_", " ") || "—"}</span>
          {p.intake_doc_source === "rtf" && (
            <span className="jde-badge" data-testid={`intake-docsrc-rtf-${p.id}`}
                  style={{ background: "#DCFCE7", color: "#166534", borderColor: "#BBF7D0", fontSize: 9 }}
                  title="Booked from the Walmart IMPORT PURCHASE ORDER RTF — the authoritative source (item#, qty, NET FIRST COST).">
              RTF
            </span>
          )}
          {p.intake_doc_source === "xlsx" && (
            <span className="jde-badge" data-testid={`intake-docsrc-xlsx-${p.id}`}
                  style={{ background: "#FEF3C7", color: "#92400E", borderColor: "#FDE68A", fontSize: 9 }}
                  title="Booked/enriched from the DC-distribution spreadsheet (fallback — no parseable RTF was available). Attach the RTF for authoritative prices.">
              xlsx fallback
            </span>
          )}
          {p.erp_desc_filled && (
            <span className="jde-badge" data-testid={`intake-docsrc-erpdesc-${p.id}`}
                  style={{ background: "#DBEAFE", color: "#1E40AF", borderColor: "#BFDBFE", fontSize: 9 }}
                  title="Line descriptions were resolved from the ERP item master (dbo.item.iname) by VSN / item number.">
              ERP desc
            </span>
          )}
        </span>
      ) },
    { key: "action", header: "", sortable: false,
      render: (p) => (
        view === "pushed" ? (
          <button className="jde-btn jde-btn-sm" data-testid={`correct-plant-${p.id}`}
                  onClick={(e) => { e.stopPropagation(); setCorrectPo(p); }}
                  title="Correction to POs routed to the wrong customer/plant">
            Correct customer
          </button>
        ) : view === "confirmed" ? (
          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
            <button className="jde-btn jde-btn-sm" data-testid={`review-${p.id}`}
                    onClick={(e) => { e.stopPropagation(); setActive(p); }}>
              Review
            </button>
            {(p.older_versions || []).length > 0 ? (
              <button className="jde-btn jde-btn-sm" data-testid={`push-overwrite-${p.id}`}
                      style={{ background: "#EA580C", color: "#fff", borderColor: "#EA580C" }}
                      onClick={(e) => { e.stopPropagation(); setPushPo({ id: p.id, po_number: p.po_number, overwrite_older: true }); }}
                      title={`Push this PO to the ERP and supersede ${p.older_versions.length} older version(s) of the same customer PO`}>
                Push &amp; overwrite older ({p.older_versions.length})
              </button>
            ) : (
              <button className="jde-btn jde-btn-primary jde-btn-sm" data-testid={`push-erp-${p.id}`}
                      onClick={(e) => { e.stopPropagation(); setPushPo({ id: p.id, po_number: p.po_number }); }}
                      title="Push this confirmed PO to MS SQL (ERP)">
                Push to ERP
              </button>
            )}
          </div>
        ) : (
          <button className="jde-btn jde-btn-sm" data-testid={`review-${p.id}`}
                  onClick={(e) => { e.stopPropagation(); setActive(p); }}>
            Review
          </button>
        )
      ) },
  ];

  return (
    <Layout breadcrumb={["Home", "PO Intake Review"]}>
      <div className="jde-page">
        <div className="jde-panel" data-testid="intake-review-panel">
          <div className="jde-panel-header">
            <div className="jde-panel-title">
              {view === "pushed" ? "PUSHED POs — CORRECT CUSTOMER/PLANT"
                : view === "confirmed" ? "CONFIRMED POs — READY TO PUSH TO ERP"
                : "PO INTAKE REVIEW"} ({rows.length}{loading ? "…" : ""})
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="jde-btn jde-btn-primary jde-btn-sm" onClick={() => navigate("/po/new")} data-testid="intake-new-po">
                + New PO
              </button>
              <button className="jde-btn jde-btn-sm" onClick={pollO365} disabled={polling} data-testid="intake-poll-o365">
                {polling ? "Polling O365…" : "Poll O365"}
              </button>
              <input ref={dcFileRef} type="file" accept=".xlsx,.xls" style={{ display: "none" }}
                     onChange={enrichFromDc} data-testid="intake-dc-file-input" />
              <button className="jde-btn jde-btn-sm" onClick={() => dcFileRef.current?.click()} disabled={enriching}
                      data-testid="intake-enrich-dc"
                      title="Enrichment runs automatically when the DC Distribution workbook is attached to the Walmart email. Use this to manually re-run it from a DC Excel file.">
                {enriching ? "Enriching…" : "Enrich from DC Excel"}
              </button>
              <input ref={po2FileRef} type="file" accept=".xlsx,.xls" style={{ display: "none" }}
                     onChange={importCustPo2} data-testid="intake-custpo2-file-input" />
              <button className="jde-btn jde-btn-sm" onClick={() => po2FileRef.current?.click()} disabled={importingPo2}
                      data-testid="intake-import-custpo2"
                      title="Import End-Customer PO Numbers from a 2-column Excel (Column A = PO#, Column B = End-Customer PO Number). Matches intake AND ERP POs — updates Open Orders, POs, OTD Risk.">
                {importingPo2 ? "Importing…" : "Import End-Customer PO"}
              </button>
              <input ref={legrandErpFileRef} type="file" accept=".xlsx,.xls" style={{ display: "none" }}
                     onChange={writeLegrandEndcustErp} data-testid="legrand-erp-file-input" />
              <button className="jde-btn jde-btn-sm" onClick={() => legrandErpFileRef.current?.click()} disabled={writingErp}
                      data-testid="legrand-write-endcust-erp"
                      title="Write the End-Customer PO into CustPO2 in the LIVE ERP (dbo.po/gst/po_dispatch) for OPEN Legrand POs. Previews first, then confirms. CustPO is never changed.">
                {writingErp ? "Writing to ERP…" : "Write End-Cust PO → ERP (Legrand)"}
              </button>
              <button className="jde-btn jde-btn-sm" onClick={recoverSources} disabled={recovering} data-testid="intake-recover-sources" title="Re-download source documents from O365 for POs missing a preview">
                {recovering ? "Recovering…" : "Re-fetch missing sources"}
              </button>
              {(view === "confirmed" || view === "review") && (
                <button className="jde-btn jde-btn-sm" onClick={reconcilePushed} disabled={reconciling} data-testid="intake-reconcile-pushed"
                        title="Find any intake PO (Review queue or Confirmed) that is ALREADY booked in the live ERP (matches Customer PO against dbo.po) and move it to 'Pushed POs' so it leaves this list. Previews first, then confirms. Reversible.">
                  {reconciling ? "Reconciling…" : "Clear already-pushed"}
                </button>
              )}
              <button className="jde-btn jde-btn-sm" onClick={load} data-testid="intake-refresh">Refresh</button>
              <button className="jde-btn jde-btn-sm" onClick={() => navigate("/intake-audit")} data-testid="intake-view-audit"
                      title="View the history of intake review actions (confirm / reject / edit) with who and when.">
                View Audit Log &rarr;
              </button>
            </div>
          </div>
          <div className="jde-panel-body" style={{ padding: 0 }}>
            <div style={{ display: "flex", gap: 0, borderBottom: "1px solid var(--omh-divider)", background: "#f8fafc" }}
                 data-testid="intake-view-tabs">
              {[
                ["review", "Review queue"],
                ["confirmed", "Confirmed (to push)"],
                ["pushed", "Pushed POs"],
                ["price", "Price Check"],
                ["history", "Price History"],
              ].map(([v, label]) => (
                <button key={v} data-testid={`intake-view-${v}`} onClick={() => setView(v)}
                        style={{
                          appearance: "none", border: "none", cursor: "pointer",
                          padding: "10px 18px", fontSize: 13, fontWeight: view === v ? 700 : 500,
                          background: "transparent", color: view === v ? "var(--omh-primary)" : "#64748b",
                          borderBottom: view === v ? "3px solid var(--omh-primary)" : "3px solid transparent",
                          transition: "color .15s, border-color .15s",
                        }}>
                  {label}
                </button>
              ))}
            </div>
            {view === "confirmed" && rows.length > 0 && (
              <div style={{ display: "flex", justifyContent: "flex-end", padding: "10px 14px",
                            borderBottom: "1px solid var(--omh-divider)", background: "#fff" }}>
                <button className="jde-btn jde-btn-primary jde-btn-sm" onClick={pushAllConfirmed}
                        disabled={pushingAll} data-testid="intake-push-all-confirmed"
                        title="Push every confirmed PO into the ERP at once (previews first, then confirms).">
                  {pushingAll ? "Pushing all…" : `Push all confirmed (${rows.length})`}
                </button>
              </div>
            )}
            {view === "history" ? (
              <div data-testid="intake-price-history">
                <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--omh-divider)", background: "#fff",
                              display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <input className="jde-input" style={{ maxWidth: 260 }} placeholder="Item code or name…" value={histQ}
                         onChange={(e) => setHistQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadHistory(1)} data-testid="hist-q" />
                  <input className="jde-input" style={{ maxWidth: 220 }} placeholder="Customer (optional)…" value={histCustomer}
                         onChange={(e) => setHistCustomer(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadHistory(1)} data-testid="hist-customer" />
                  <button className="jde-btn jde-btn-primary jde-btn-sm" onClick={() => loadHistory(1)} data-testid="hist-search">Search</button>
                  <button className="jde-btn jde-btn-sm" onClick={downloadHistoryCsv} disabled={histDownloading || !((histData?.rows || []).length)} data-testid="hist-download">
                    {histDownloading ? "Preparing…" : "Download CSV"}
                  </button>
                  <span style={{ fontSize: 12, color: "#64748b" }}>PO-based price history per item + customer. Rows marked when the agreed PO price changed vs the prior PO.</span>
                  {histData && <span style={{ marginLeft: "auto", fontSize: 12, color: "#64748b" }}>{histData.total} item/customer series</span>}
                </div>
                {histLoading ? (
                  <div style={{ padding: 30, fontSize: 13 }}>Loading price history…</div>
                ) : !((histData?.rows || []).length) ? (
                  <div style={{ padding: 30, fontSize: 13 }}>No PO price history found{histQ || histCustomer ? " for this search" : ""}.</div>
                ) : (
                  <>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }} data-testid="price-history-table">
                      <thead>
                        <tr style={{ background: "#f8fafc", textAlign: "left", color: "#64748b", fontSize: 12 }}>
                          <th style={{ padding: "8px 14px" }}>Item</th>
                          <th style={{ padding: "8px 10px" }}>Description</th>
                          <th style={{ padding: "8px 10px" }}>Customer</th>
                          <th style={{ padding: "8px 10px", textAlign: "right" }}>POs</th>
                          <th style={{ padding: "8px 10px", textAlign: "right" }}>First</th>
                          <th style={{ padding: "8px 10px", textAlign: "right" }}>Latest</th>
                          <th style={{ padding: "8px 10px" }}>Price changes</th>
                          <th style={{ padding: "8px 14px", width: 34 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {histData.rows.map((r) => {
                          const key = `${r.item}|${r.customer}|${r.currency}`;
                          const hasChanges = r.change_count > 0;
                          const open = hasChanges && !!histExpanded[key];
                          return (
                            <Fragment key={key}>
                              <tr data-testid={`hist-row-${r.item}`} onClick={() => hasChanges && setHistExpanded((e) => ({ ...e, [key]: !e[key] }))}
                                  style={{ cursor: hasChanges ? "pointer" : "default", borderTop: "1px solid var(--omh-divider)", background: open ? "#fafafa" : "#fff" }}>
                                <td style={{ padding: "8px 14px", fontWeight: 600 }}>{r.item}</td>
                                <td style={{ padding: "8px 10px", color: "#475569", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.name}>{r.name || "—"}</td>
                                <td style={{ padding: "8px 10px" }}>{r.customer || "—"}</td>
                                <td style={{ padding: "8px 10px", textAlign: "right" }}>{r.po_count}</td>
                                <td style={{ padding: "8px 10px", textAlign: "right" }} className="text-mono">{fmtCur(r.first_price, r.currency)}</td>
                                <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 600 }} className="text-mono">{fmtCur(r.latest_price, r.currency)}</td>
                                <td style={{ padding: "8px 10px" }}>
                                  {hasChanges ? (
                                    <span style={{ fontSize: 11, fontWeight: 700, color: "#b45309", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 999, padding: "2px 9px" }}>
                                      {r.change_count} change{r.change_count > 1 ? "s" : ""}
                                    </span>
                                  ) : <span style={{ fontSize: 11, fontWeight: 600, color: "#15803d" }}>Stable</span>}
                                </td>
                                <td style={{ padding: "8px 14px", color: "#94a3b8" }}>{hasChanges ? (open ? "▾" : "▸") : ""}</td>
                              </tr>
                              {open && (
                                <tr>
                                  <td colSpan={8} style={{ padding: "0 14px 12px", background: "#fafafa" }}>
                                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, background: "#fff", border: "1px solid var(--omh-divider)" }}>
                                      <thead>
                                        <tr style={{ background: "#f1f5f9", textAlign: "left", color: "#64748b" }}>
                                          <th style={{ padding: "6px 10px" }}>Effective date</th>
                                          <th style={{ padding: "6px 10px" }}>PO #</th>
                                          <th style={{ padding: "6px 10px", textAlign: "right" }}>Total Qty</th>
                                          <th style={{ padding: "6px 10px", textAlign: "right" }}>Price</th>
                                          <th style={{ padding: "6px 10px", textAlign: "right" }}>Was</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {r.changes.slice().reverse().map((p, i) => {
                                          const isBaseline = p.from_price == null;
                                          return (
                                            <tr key={`${p.invoice_no}-${i}`} style={{ borderTop: "1px solid var(--omh-divider)", background: isBaseline ? "#fff" : "#FFFBEB" }}>
                                              <td style={{ padding: "6px 10px" }}>{p.date || "—"}</td>
                                              <td style={{ padding: "6px 10px", fontWeight: 600 }} data-testid={`hist-po-${p.pono || p.invoice_no}`}>{p.pono || "—"}</td>
                                              <td style={{ padding: "6px 10px", textAlign: "right" }}>{fmtNum(p.qty)}</td>
                                              <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 700, color: isBaseline ? "#334155" : "#b45309" }} className="text-mono">{fmtCur(p.price, r.currency)}</td>
                                              <td style={{ padding: "6px 10px", textAlign: "right", color: "#94a3b8" }} className="text-mono">{isBaseline ? "—" : fmtCur(p.from_price, r.currency)}</td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                    {histData.pages > 1 && (
                      <div style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "center", padding: "12px" }}>
                        <button className="jde-btn jde-btn-sm" disabled={histPage <= 1} onClick={() => loadHistory(histPage - 1)}>‹ Prev</button>
                        <span style={{ fontSize: 12.5, color: "#64748b" }}>Page {histData.page} of {histData.pages}</span>
                        <button className="jde-btn jde-btn-sm" disabled={histPage >= histData.pages} onClick={() => loadHistory(histPage + 1)}>Next ›</button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : view === "price" ? (
              <div data-testid="intake-price-check">
                <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--omh-divider)", background: "#fff",
                              fontSize: 12.5, color: "#475569", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                  <span>Each line's price vs the <b>last booked price</b> for the same item (prefers the same customer). Flagged when the change is ≥ {priceData?.flag_threshold_pct ?? 5}%.</span>
                  <button className="jde-btn jde-btn-sm" onClick={loadPriceCheck} data-testid="price-check-refresh">Refresh</button>
                </div>
                {priceLoading ? (
                  <div style={{ padding: 30, fontSize: 13 }}>Loading price check…</div>
                ) : !((priceData?.pos || []).length) ? (
                  <div style={{ padding: 30, fontSize: 13 }}>No purchase orders are awaiting review.</div>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }} data-testid="price-check-table">
                    <thead>
                      <tr style={{ background: "#f8fafc", textAlign: "left", color: "#64748b", fontSize: 12 }}>
                        <th style={{ padding: "8px 14px" }}>PO #</th>
                        <th style={{ padding: "8px 10px" }}>Customer</th>
                        <th style={{ padding: "8px 10px" }}>PO date</th>
                        <th style={{ padding: "8px 10px", textAlign: "right" }}>Priced lines</th>
                        <th style={{ padding: "8px 10px" }}>Price changes</th>
                        <th style={{ padding: "8px 14px", width: 34 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {priceData.pos.map((p) => {
                        const open = !!priceExpanded[p.id];
                        return (
                          <Fragment key={p.id}>
                            <tr data-testid={`price-po-${p.id}`} onClick={() => setPriceExpanded((e) => ({ ...e, [p.id]: !e[p.id] }))}
                                style={{ cursor: "pointer", borderTop: "1px solid var(--omh-divider)", background: open ? "#fafafa" : "#fff" }}>
                              <td style={{ padding: "8px 14px", fontWeight: 600 }}>{p.po_number || "—"}</td>
                              <td style={{ padding: "8px 10px" }}>{p.customer_name || "—"}</td>
                              <td style={{ padding: "8px 10px" }}>{p.order_date || "—"}</td>
                              <td style={{ padding: "8px 10px", textAlign: "right" }}>{p.priced_lines}/{p.line_count}</td>
                              <td style={{ padding: "8px 10px" }}>
                                {p.change_count > 0 ? (
                                  <span data-testid={`price-po-flag-${p.id}`} style={{ fontSize: 11, fontWeight: 700, color: "#b45309", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 999, padding: "2px 9px" }}>
                                    {p.change_count} change{p.change_count > 1 ? "s" : ""}
                                  </span>
                                ) : p.priced_lines > 0 ? (
                                  <span style={{ fontSize: 11, fontWeight: 600, color: "#15803d", background: "#ECFDF5", border: "1px solid #A7F3D0", borderRadius: 999, padding: "2px 9px" }}>In line</span>
                                ) : (
                                  <span style={{ fontSize: 11, color: "#94a3b8" }}>No history</span>
                                )}
                              </td>
                              <td style={{ padding: "8px 14px", color: "#94a3b8" }}>{open ? "▾" : "▸"}</td>
                            </tr>
                            {open && (
                              <tr>
                                <td colSpan={6} style={{ padding: "0 14px 12px", background: "#fafafa" }}>
                                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, background: "#fff", border: "1px solid var(--omh-divider)" }}>
                                    <thead>
                                      <tr style={{ background: "#f1f5f9", textAlign: "left", color: "#64748b" }}>
                                        <th style={{ padding: "6px 10px" }}>Item</th>
                                        <th style={{ padding: "6px 10px" }}>Description</th>
                                        <th style={{ padding: "6px 10px", textAlign: "right" }}>Qty</th>
                                        <th style={{ padding: "6px 10px", textAlign: "right" }}>Current</th>
                                        <th style={{ padding: "6px 10px", textAlign: "right" }}>Last</th>
                                        <th style={{ padding: "6px 10px", textAlign: "right" }}>Δ%</th>
                                        <th style={{ padding: "6px 10px" }}>Last seen</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {p.lines.map((l, i) => (
                                        <tr key={`${p.id}-${l.sku}-${i}`} style={{ borderTop: "1px solid var(--omh-divider)", background: l.flagged ? "#FFFBEB" : "#fff" }}>
                                          <td style={{ padding: "6px 10px", fontWeight: 600 }}>{l.sku || "—"}</td>
                                          <td style={{ padding: "6px 10px", color: "#475569" }}>{l.description || "—"}</td>
                                          <td style={{ padding: "6px 10px", textAlign: "right" }}>{fmtNum(l.qty)}</td>
                                          <td style={{ padding: "6px 10px", textAlign: "right" }}>{l.current_price ? fmtNum(l.current_price) : "—"}</td>
                                          <td style={{ padding: "6px 10px", textAlign: "right" }}>{l.has_history ? fmtNum(l.last_price) : <span style={{ color: "#94a3b8" }}>no history</span>}</td>
                                          <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: l.flagged ? 700 : 500,
                                                       color: l.delta_pct == null ? "#94a3b8" : l.delta_pct > 0 ? "#b45309" : l.delta_pct < 0 ? "#dc2626" : "#15803d" }}>
                                            {l.delta_pct == null ? "—" : `${l.delta_pct > 0 ? "+" : ""}${l.delta_pct}%`}
                                          </td>
                                          <td style={{ padding: "6px 10px", color: "#64748b", fontSize: 11.5 }}>
                                            {l.has_history ? (
                                              <>PO {l.last_po_number || "—"}{l.last_date ? ` · ${l.last_date}` : ""}{!l.same_customer ? <span style={{ color: "#b45309" }}> · other customer</span> : ""}</>
                                            ) : "—"}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            ) : (
            loading ? (
              <div data-testid="intake-loading" style={{ padding: 30, fontSize: 13, color: "#64748b" }}>Loading POs…</div>
            ) : (
            <SortableGrid pageSize={100}
              columns={columns}
              rows={rows}
              getRowKey={(p) => p.id}
              onRowClick={(p) => (view === "pushed" ? setCorrectPo(p) : setActive(p))}
              defaultSort={{ key: "order_date", dir: "desc" }}
              searchPlaceholder="Search PO #, customer…"
              testid="intake-review-table"
              emptyMessage={view === "pushed" ? "No pushed POs found."
                : view === "confirmed" ? "No confirmed POs waiting to be pushed."
                : "No purchase orders are awaiting review."}
            />
            )
            )}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12,
                      padding: "16px 4px 4px", flexWrap: "wrap" }}>
          <span className="muted" style={{ fontSize: 12, maxWidth: 620, textAlign: "right" }}>
            Fixes Walmart Import POs that booked the <b>carton count</b> instead of ordered <b>eaches</b>
            — re-reads each PO's source (QUANTITY (EA.) column). Already-pushed POs are audited, not changed.
          </span>
          <button className="jde-btn jde-btn-sm" onClick={reparseWalmartQty} disabled={reparsing}
                  data-testid="intake-reparse-walmart-qty"
                  title="Re-derive ordered eaches from each Walmart Import PO's source; previews first, then applies to pending POs.">
            {reparsing ? "Re-parsing…" : "Re-parse Walmart quantities from source"}
          </button>
        </div>
      </div>

      {active && (
        <ReviewModal po={active} saving={saving} onClose={() => setActive(null)}
                     onConfirm={(payload) => confirm(active, payload)}
                     onConfirmAndPush={(payload) => confirmAndPush(active, payload)}
                     onDelete={() => remove(active)} />
      )}

      {pushPo && (
        <PushToErpDialog poId={pushPo.id} poNumber={pushPo.po_number}
                         overwriteOlder={!!pushPo.overwrite_older}
                         onClose={() => setPushPo(null)}
                         onDone={() => { setPushPo(null); load(); }} />
      )}

      {correctPo && (
        <CorrectPlantDialog po={correctPo} onClose={() => setCorrectPo(null)}
                            onDone={() => { setCorrectPo(null); load(); }} />
      )}
    </Layout>
  );
}

// Correction to POs routed to the wrong customer/plant. Re-routes an already-pushed
// intake PO from its current ERP plant to the one the user picks, re-pushing under the
// corrected Pcode. Runs a dry-run preview first (shows current → target + any blockers),
// then confirms. Blocks if the PO already has dispatches/invoices booked.
function CorrectPlantDialog({ po, onClose, onDone }) {
  const [pcode, setPcode] = useState("");
  const [custName, setCustName] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [erp, setErp] = useState(null); // true current ERP plant (read-only, on open)
  const [erpLoading, setErpLoading] = useState(true);

  // Read the ACTUAL current ERP plant on open — the Mongo customer_name shown in the list
  // can be stale, so we must show where the PO really is in dbo.po to avoid contradictions.
  useEffect(() => {
    let alive = true;
    setErpLoading(true);
    api.get(`/po/${po.id}/erp-plant`)
      .then((r) => { if (alive) setErp(r.data); })
      .catch(() => { if (alive) setErp({ error: true }); })
      .finally(() => { if (alive) setErpLoading(false); });
    return () => { alive = false; };
  }, [po.id]);

  const runDryRun = async (chosenPcode) => {
    if (!chosenPcode) return;
    setBusy(true); setPreview(null);
    try {
      const { data } = await api.post(`/po/${po.id}/correct-plant`, { new_pcode: chosenPcode, confirm: false });
      setPreview(data);
    } catch (e) {
      // Degrade gracefully: surface the reason inline instead of only a toast.
      setPreview({ blockers: [e.response?.data?.detail || "Could not validate the correction."], stage: "error" });
    } finally { setBusy(false); }
  };

  const confirmCorrection = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/po/${po.id}/correct-plant`, { new_pcode: pcode, confirm: true });
      if (data.ok && data.stage === "corrected") {
        toast.success(`PO ${po.po_number} re-routed ${data.current_pcode} → ${data.target_pcode} (ERP PoNo ${data.pono})`);
        onDone();
      } else {
        toast.error((data.blockers || []).join(" ") || "Correction was blocked");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Correction failed");
    } finally { setBusy(false); }
  };

  // Fix a stale dashboard label so the table matches the ERP (no ERP write).
  const fixLabel = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/po/${po.id}/resync-erp-label`);
      toast.success(`Label updated to ${data.pcode} — ${data.customer_name}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not fix the label");
    } finally { setBusy(false); }
  };

  const blocked = preview && (preview.blockers || []).length > 0;
  const canConfirm = preview && preview.stage === "dry-run" && !blocked && pcode;
  const erpLabel = erp && erp.in_erp
    ? `${erp.current_pcode}${erp.current_party ? ` — ${erp.current_party}` : ""}`
    : null;
  const stale = erp && erp.in_erp && erp.mongo_customer_name
    && (erp.current_party || "").trim().toUpperCase() !== (erp.mongo_customer_name || "").trim().toUpperCase();

  return (
    <div className="omh-modal-overlay" data-testid="correct-plant-modal"
         style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 1000,
                  display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "60px 20px", overflowY: "auto" }}
         onClick={onClose}>
      <div className="jde-panel" style={{ width: "min(560px, 100%)", background: "#fff" }} onClick={(e) => e.stopPropagation()}>
        <div className="jde-panel-header">
          <div className="jde-panel-title">CORRECTION TO POs ROUTED TO WRONG CUSTOMER</div>
          <button className="jde-btn jde-btn-sm" onClick={onClose} data-testid="correct-plant-close">Close</button>
        </div>
        <div className="jde-panel-body">
          <div style={{ fontSize: 13, marginBottom: 12 }}>
            <div><strong>PO #:</strong> <span className="text-mono">{po.po_number}</span> · ERP PoNo <span className="text-mono">{erp?.pono || "—"}</span></div>
            <div data-testid="correct-plant-current">
              <strong>Currently in ERP:</strong>{" "}
              {erpLoading ? <span className="muted">reading ERP…</span>
                : erp?.error ? <span className="muted">could not read ERP</span>
                : erp?.in_erp ? <span className="text-mono" style={{ fontWeight: 700 }}>{erpLabel}</span>
                : <span className="muted">not found in ERP</span>}
            </div>
            {stale && (
              <div style={{ marginTop: 6, fontSize: 11.5, color: "#92400E", background: "#FEF3C7",
                            border: "1px solid #FDE68A", borderRadius: 5, padding: "6px 8px" }}>
                Note: this dashboard lists it as “{erp.mongo_customer_name}”, but the ERP has it under
                {" "}<strong>{erp.current_pcode}</strong>. The ERP value above is the source of truth.
                <div style={{ marginTop: 6 }}>
                  <button className="jde-btn jde-btn-sm" onClick={fixLabel} disabled={busy}
                          data-testid="correct-plant-fix-label">
                    {busy ? "Fixing…" : `Fix label to match ERP (${erp.current_pcode})`}
                  </button>
                </div>
              </div>
            )}
          </div>

          <label className="jde-label">Correct customer / plant</label>
          <CustomerSelect name={custName} pcode={pcode} source="erp" testid="correct-plant-customer"
                          onChange={({ name, pcode: pc }) => { setCustName(name); setPcode(pc); setPreview(null); }} />
          <button className="jde-btn jde-btn-sm" style={{ marginTop: 10 }} disabled={!pcode || busy}
                  data-testid="correct-plant-validate" onClick={() => runDryRun(pcode)}>
            {busy && !preview ? "Checking…" : "Check correction"}
          </button>

          {preview && (
            <div style={{ marginTop: 14, padding: "10px 12px", borderRadius: 6,
                          background: blocked ? "#FEE2E2" : "#ECFDF5",
                          border: `1px solid ${blocked ? "#FCA5A5" : "#A7F3D0"}` }}
                 data-testid="correct-plant-preview">
              {blocked ? (
                <div style={{ color: "#991B1B", fontSize: 12.5, fontWeight: 600 }}>
                  {(preview.blockers || []).map((b, i) => <div key={i}>• {b}</div>)}
                </div>
              ) : (
                <div style={{ fontSize: 13 }}>
                  <div><strong>Re-route:</strong> {preview.current_pcode} ({preview.current_party || "—"}) → <strong>{preview.target_pcode}</strong> ({preview.target_party || "—"})</div>
                  <div style={{ marginTop: 4 }} className="muted">ERP PoNo {preview.pono} · {preview.line_count} line(s) · LineUIDs will be regenerated for the new plant.</div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="jde-panel-body" style={{ borderTop: "1px solid var(--omh-divider)", display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button className="jde-btn" onClick={onClose} data-testid="correct-plant-cancel">Cancel</button>
          <button className="jde-btn jde-btn-primary" disabled={!canConfirm || busy}
                  onClick={confirmCorrection} data-testid="correct-plant-confirm"
                  title={!canConfirm ? "Run a check with no blockers first" : undefined}>
            {busy ? "Correcting…" : "Confirm correction"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DocViewer({ poId }) {
  const [url, setUrl] = useState(null);
  const [mime, setMime] = useState("");
  const [fname, setFname] = useState("");
  const [text, setText] = useState("");        // extracted text for RTF/DOC/DOCX/TXT/EML
  const [state, setState] = useState("loading"); // loading | ok | none | error
  const [refetching, setRefetching] = useState(false);

  const isSheet = /spreadsheet|excel|ms-excel/i.test(mime) ||
                  /\.(xlsx|xlsm|xls)$/i.test(fname);

  const load = () => {
    let objUrl;
    let cancelled = false;
    setState("loading");
    setText(""); setFname("");
    api.get(`/po/${poId}/file`, { responseType: "blob" })
      .then(async (r) => {
        if (cancelled) return;
        objUrl = URL.createObjectURL(r.data);
        setUrl(objUrl);
        const m = r.data.type || "";
        setMime(m);
        // Browsers can only render PDFs & images inline; everything else (RTF/DOC/DOCX/TXT/EML)
        // is fetched as plain text so it previews instead of triggering a download. Spreadsheets
        // are never previewed inline (they are the DC workbook, not the PO doc).
        const inlineOk = m.startsWith("image/") || m === "application/pdf";
        const looksSheet = /spreadsheet|excel|ms-excel/i.test(m);
        if (!inlineOk && !looksSheet) {
          try {
            const { data } = await api.get(`/po/${poId}/file-text`);
            if (!cancelled) { setText(data.text || ""); setFname(data.filename || ""); }
          } catch { /* fall back to the message below */ }
        } else if (looksSheet) {
          try {
            const { data } = await api.get(`/po/${poId}/file-text`);
            if (!cancelled) setFname(data.filename || "");
          } catch { /* ignore */ }
        }
        if (!cancelled) setState("ok");
      })
      .catch((e) => {
        if (cancelled) return;
        setState(e.response?.status === 404 ? "none" : "error");
      });
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  };

  useEffect(() => load(), [poId]);

  const refetch = async () => {
    setRefetching(true);
    try {
      const { data } = await api.post(`/po/${poId}/refetch-source`);
      if (data.po_has_source) { toast.success("PO document refreshed from the source email"); load(); }
      else toast.error("No matching PO document found in the source email");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not re-fetch source from email");
    } finally { setRefetching(false); }
  };

  const RefetchBtn = ({ label }) => (
    <button data-testid="review-doc-refetch" onClick={refetch} disabled={refetching}
      style={{ marginTop: 12, padding: "7px 14px", border: "1px solid var(--omh-primary)", borderRadius: 6, background: "white", color: "var(--omh-primary)", fontSize: 12, fontWeight: 700, cursor: refetching ? "default" : "pointer" }}>
      {refetching ? "Fetching…" : (label || "Re-fetch source from email")}
    </button>
  );

  if (state === "loading") return <div className="muted" style={{ padding: 20, textAlign: "center" }}>Loading document…</div>;
  if (state === "none") return <div style={{ padding: 20, textAlign: "center" }} data-testid="review-doc-none"><div className="muted">No source document on file for this PO.</div><RefetchBtn /></div>;
  if (state === "error") return <div style={{ padding: 20, textAlign: "center" }}><div className="muted" style={{ color: "var(--omh-alert)" }}>Could not load the document.</div><RefetchBtn /></div>;
  const isImg = mime.startsWith("image/");
  const isPdf = mime === "application/pdf";
  return (
    <div data-testid="review-doc-viewer" style={{ border: "1px solid var(--omh-divider)", borderRadius: 6, overflow: "hidden", background: "#f8fafc" }}>
      {isSheet
        ? <div data-testid="review-doc-spreadsheet" style={{ padding: 20, textAlign: "center" }}>
            <div style={{ fontWeight: 700, color: "#92400E", marginBottom: 4 }}>
              The stored source for this PO is the DC distribution spreadsheet, not the PO document.
            </div>
            <div className="muted" style={{ fontSize: 12.5 }}>
              Spreadsheets can&apos;t be shown inline. Click below to pull the RTF/PDF PO copy from the
              source email, or open the spreadsheet in a new tab.
            </div>
            <RefetchBtn label="Fetch the RTF PO copy from email" />
          </div>
        : isImg
          ? <img src={url} alt="PO document" style={{ width: "100%", maxHeight: 520, objectFit: "contain", display: "block" }} />
          : isPdf
            ? <iframe title="PO document" src={url} style={{ width: "100%", height: 520, border: "none" }} />
            : text
              ? <pre data-testid="review-doc-text" style={{ margin: 0, padding: 16, maxHeight: 520, overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "Inter, sans-serif", fontSize: 12, lineHeight: 1.55, color: "#1f2937", background: "#fff" }}>{text}</pre>
              : <div className="muted" data-testid="review-doc-notext" style={{ padding: 20, textAlign: "center" }}>This document format can&apos;t be previewed inline. Use &quot;Open in new tab&quot; to view it.<RefetchBtn /></div>}
      {!isSheet && (
        <div style={{ padding: 8, textAlign: "right" }}>
          <a href={url} target="_blank" rel="noreferrer" style={{ color: "var(--omh-primary)", fontSize: 12 }}>Open in new tab ↗</a>
        </div>
      )}
    </div>
  );
}

function AuditTrail({ poId }) {
  const [rows, setRows] = useState([]);
  const [state, setState] = useState("loading"); // loading | ok | error

  useEffect(() => {
    let cancelled = false;
    setState("loading");
    api.get(`/po/${poId}/audit`)
      .then((r) => { if (!cancelled) { setRows(r.data || []); setState("ok"); } })
      .catch(() => { if (!cancelled) setState("error"); });
    return () => { cancelled = true; };
  }, [poId]);

  const badge = {
    edit: { bg: "#FEF3C7", fg: "#92400E", label: "EDIT" },
    confirm: { bg: "#DCFCE7", fg: "#166534", label: "CONFIRM" },
    reject: { bg: "#FEE2E2", fg: "#991B1B", label: "REJECT" },
    delete: { bg: "#FEE2E2", fg: "#991B1B", label: "DELETE" },
  };

  return (
    <div data-testid="review-audit-trail">
      <div style={{ fontSize: 12, fontWeight: 700, color: "var(--omh-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", margin: "22px 0 6px" }}>
        Audit Trail
      </div>
      {state === "loading" && <div className="muted" style={{ fontSize: 12, padding: 8 }}>Loading audit trail…</div>}
      {state === "error" && <div className="muted" style={{ fontSize: 12, padding: 8, color: "var(--omh-alert)" }}>Could not load the audit trail.</div>}
      {state === "ok" && rows.length === 0 && (
        <div className="muted" style={{ fontSize: 12, padding: 8 }} data-testid="review-audit-empty">
          No review activity recorded yet for this PO.
        </div>
      )}
      {state === "ok" && rows.length > 0 && (
        <div style={{ border: "1px solid var(--omh-divider)", borderRadius: 6, overflow: "hidden" }}>
          {rows.map((a) => {
            const b = badge[a.action] || { bg: "#E2E8F0", fg: "#334155", label: (a.action || "").toUpperCase() };
            return (
              <div key={a.id} data-testid={`audit-entry-${a.id}`}
                   style={{ padding: "10px 12px", borderBottom: "1px solid var(--omh-divider)", background: "#fff" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span className="jde-badge" style={{ background: b.bg, color: b.fg, fontWeight: 700, fontSize: 10 }}>{b.label}</span>
                  <strong style={{ fontSize: 12 }}>{a.actor || "system"}</strong>
                  {a.actor_role && <span className="muted" style={{ fontSize: 10.5 }}>({a.actor_role})</span>}
                  <span className="muted text-mono" style={{ fontSize: 11, marginLeft: "auto" }}>{fmtDate(a.timestamp) || a.timestamp}</span>
                </div>
                {a.note && <div style={{ fontSize: 12, marginTop: 5, color: "var(--omh-alert)" }}>Reason: {a.note}</div>}
                {(a.changes || []).length > 0 && (
                  <table className="jde-grid" style={{ width: "100%", marginTop: 6 }}>
                    <thead>
                      <tr>
                        <th style={{ fontSize: 10 }}>Field</th>
                        <th style={{ fontSize: 10 }}>From</th>
                        <th style={{ fontSize: 10 }}>To</th>
                      </tr>
                    </thead>
                    <tbody>
                      {a.changes.map((c, i) => (
                        <tr key={i}>
                          <td style={{ fontSize: 11 }}>{c.field}</td>
                          <td style={{ fontSize: 11, color: "#94A3B8", textDecoration: "line-through" }}>{c.old ?? "—"}</td>
                          <td style={{ fontSize: 11, fontWeight: 700 }}>{c.new ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ReviewModal({ po, saving, onClose, onConfirm, onConfirmAndPush, onDelete }) {
  const dateOnly = (d) => (d || "").slice(0, 10);
  const initForm = (p) => ({
    customer_name: p.customer_name || "", portal_pcode: p.portal_pcode || p.resolved_pcode || "",
    customer_email: p.customer_email || "", po_number: p.po_number || "",
    po_revision: p.po_revision || "",
    cust_po2: p.cust_po2 || "",
    order_date: dateOnly(p.order_date), currency: p.currency || "USD", notes: p.notes || "",
    ship_to_address: p.ship_to_address || "", bill_to_address: p.bill_to_address || "",
    line_items: (p.line_items || []).map((l) => ({
      sku: l.sku || "", item_number: l.item_number || "", rev: l.rev || "", description: l.description || "",
      quantity_ordered: l.quantity_ordered ?? "", unit_price: l.unit_price ?? "",
      uom: l.uom || "EA", due_date: dateOnly(l.due_date),
    })),
  });
  const [f, setF] = useState(() => initForm(po));
  useEffect(() => { setF(initForm(po)); /* eslint-disable-next-line */ }, [po.id]);

  // Previous-PO price per SKU (quick discrepancy eyeball) — fetched once when the modal opens.
  const [prevPrices, setPrevPrices] = useState({});
  useEffect(() => {
    let alive = true;
    api.get(`/po/${po.id}/price-baseline`)
      .then((r) => { if (alive) setPrevPrices(r.data?.skus || {}); })
      .catch(() => { if (alive) setPrevPrices({}); });
    return () => { alive = false; };
  }, [po.id]);
  const prevFor = (sku) => prevPrices[(sku || "").trim().toUpperCase()];

  const setH = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setLine = (i, k, v) => setF((p) => ({ ...p, line_items: p.line_items.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)) }));
  const pickItem = (i, it) => setF((p) => ({ ...p, line_items: p.line_items.map((l, idx) => (idx === i ? { ...l, sku: it.icode, description: it.iname, uom: it.unit || "EA" } : l)) }));
  const addLine = () => setF((p) => ({ ...p, line_items: [...p.line_items, { sku: "", item_number: "", rev: "", description: "", quantity_ordered: "", unit_price: "", uom: "EA", due_date: "" }] }));
  const removeLine = (i) => setF((p) => ({ ...p, line_items: p.line_items.filter((_, idx) => idx !== i) }));

  const lineTotal = (l) => (Number(l.quantity_ordered) || 0) * (Number(l.unit_price) || 0);
  const poTotal = f.line_items.reduce((s, l) => s + lineTotal(l), 0);
  // Intake POs must be assigned to a real customer (curated ERP code) before confirm/push.
  const custValid = !!(f.portal_pcode || "").trim();

  const buildPayload = () => {
    const valid = f.line_items.filter((l) => (l.sku || "").trim() || (l.description || "").trim());
    const dues = valid.map((l) => l.due_date).filter(Boolean).sort();
    return {
      customer_name: f.customer_name, portal_pcode: f.portal_pcode || null,
      customer_email: f.customer_email || null, po_number: f.po_number,
      cust_po2: (f.cust_po2 || "").trim() || null,
      order_date: f.order_date || null, requested_ship_date: dues[0] || null,
      currency: f.currency, notes: f.notes || null,
      ship_to_address: f.ship_to_address || null, bill_to_address: f.bill_to_address || null,
      total_amount: Number(poTotal.toFixed(2)),
      line_items: valid.map((l) => ({
        sku: (l.sku || "").trim(), item_number: (l.item_number || "").trim() || null,
        rev: (l.rev || "").trim() || null, description: l.description || "",
        quantity_ordered: Number(l.quantity_ordered) || 0,
        unit_price: l.unit_price === "" || l.unit_price == null ? null : Number(l.unit_price),
        uom: l.uom || "EA", due_date: l.due_date || null,
      })),
    };
  };

  const fld = { display: "flex", flexDirection: "column" };
  const cur = f.currency || "USD";
  const cellInput = { width: "100%", minWidth: 0, padding: "5px 6px", boxSizing: "border-box" };

  return (
    <div className="omh-modal-overlay" data-testid="review-modal"
         style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 1000,
                  display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 20px", overflowY: "auto" }}
         onClick={onClose}>
      <div className="jde-panel" style={{ width: "min(1040px, 100%)", background: "#fff", maxHeight: "90vh", overflowY: "auto" }}
           onClick={(e) => e.stopPropagation()}>
        <div className="jde-panel-header" style={{ position: "sticky", top: 0, background: "#fff", zIndex: 2 }}>
          <div className="jde-panel-title">REVIEW &amp; CONFIRM — {f.po_number}</div>
          <button className="jde-btn jde-btn-sm" onClick={onClose} data-testid="review-close">Close</button>
        </div>

        <div className="jde-panel-body">
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--omh-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
            PO Document
          </div>
          <div style={{ marginBottom: 18 }}>
            <DocViewer poId={po.id} />
          </div>

          {/* Editable header fields */}
          {!custValid && (
            <div data-testid="review-unmatched-warning"
                 style={{ marginBottom: 12, padding: "9px 12px", borderRadius: 6, background: "#FEF3C7",
                          border: "1px solid #FDE68A", color: "#92400E", fontSize: 12.5, fontWeight: 600 }}>
              This PO&apos;s customer could not be matched to your customer list. Pick the correct
              customer below before confirming — intake never creates a new customer automatically.
            </div>
          )}
          {(() => {
            const notes = po.extraction_notes || "";
            const mismatch = /\[Walmart\][^.]*PRICING MISMATCH/i.test(notes);
            const fallback = /had no RTF price/i.test(notes);
            if (!mismatch && !fallback) return null;
            return (
              <div data-testid="review-pricing-warning"
                   style={{ marginBottom: 12, padding: "9px 12px", borderRadius: 6,
                            background: mismatch ? "#FEF2F2" : "#FFFBEB",
                            border: `1px solid ${mismatch ? "#FECACA" : "#FDE68A"}`,
                            color: mismatch ? "#991B1B" : "#92400E", fontSize: 12.5, fontWeight: 600 }}>
                {mismatch
                  ? <>The RTF per-line prices don&apos;t reconcile to this PO&apos;s Total net first cost
                      (${Number(po.total_amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}).
                      Verify each unit price below before confirming or pushing to ERP.</>
                  : <>No Walmart RTF PO copy was found, so unit prices were estimated from the DC workbook&apos;s
                      PO-level <em>Total net first cost</em> (${Number(po.total_amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                      &divide; line quantity. Attach the RTF PO copy or adjust the unit prices below if needed before pushing to ERP.</>}
              </div>
            );
          })()}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 14 }}>
            <CustomerSelect name={f.customer_name} pcode={f.portal_pcode}
                            onChange={({ name, pcode }) => setF((p) => ({ ...p, customer_name: name, portal_pcode: pcode }))}
                            testid="review-customer" />
            <div style={fld}>
              <label className="jde-label">PO Number</label>
              <input className="jde-input" value={f.po_number} onChange={(e) => setH("po_number", e.target.value)} data-testid="review-po-number" />
            </div>
            {f.po_revision && (
              <div style={fld}>
                <label className="jde-label">PO Revision</label>
                <input className="jde-input" value={`REV ${f.po_revision}`} readOnly data-testid="review-po-revision"
                       style={{ background: "#F5F3FF", color: "#5B21B6", fontWeight: 700 }} />
              </div>
            )}
            <div style={fld}>
              <label className="jde-label">End Customer PO Number</label>
              <input className="jde-input" value={f.cust_po2} onChange={(e) => setH("cust_po2", e.target.value)}
                     data-testid="review-cust-po2" placeholder="End-customer PO (e.g. Legrand → 4000517529)" />
            </div>
            <div style={fld}>
              <label className="jde-label">Customer Email</label>
              <input className="jde-input" value={f.customer_email} onChange={(e) => setH("customer_email", e.target.value)} data-testid="review-cust-email" />
            </div>
            <div style={fld}>
              <label className="jde-label">PO Date</label>
              <input className="jde-input" type="date" value={f.order_date} onChange={(e) => setH("order_date", e.target.value)} data-testid="review-po-date" />
            </div>
            <div style={fld}>
              <label className="jde-label">Currency</label>
              <select className="jde-input" value={f.currency} onChange={(e) => setH("currency", e.target.value)} data-testid="review-currency">
                {["USD", "INR", "GBP", "EUR"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={fld}>
              <label className="jde-label">Source</label>
              <input className="jde-input" value={(po.source_type || "").replace("_", " ")} disabled readOnly />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 18 }}>
            <div style={fld}>
              <label className="jde-label">Ship-To Address</label>
              <textarea className="jde-textarea" rows={2} value={f.ship_to_address} onChange={(e) => setH("ship_to_address", e.target.value)} data-testid="review-ship-to" />
            </div>
            <div style={fld}>
              <label className="jde-label">Bill-To Address</label>
              <textarea className="jde-textarea" rows={2} value={f.bill_to_address} onChange={(e) => setH("bill_to_address", e.target.value)} data-testid="review-bill-to" />
            </div>
          </div>

          {/* Editable line items */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--omh-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Line Items ({f.line_items.length})
            </div>
            <button className="jde-btn jde-btn-sm" onClick={addLine} data-testid="review-add-line">+ Add line</button>
          </div>
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <table className="jde-grid" data-testid="review-lines" style={{ width: "100%", minWidth: 1040, tableLayout: "fixed" }}>
            <thead>
              <tr>
                <th style={{ width: 120 }}>VSN (SKU)</th>
                <th style={{ width: 84 }}>Item #</th>
                <th style={{ width: 160 }}>Description</th>
                <th style={{ width: 96, textAlign: "right" }}>Qty</th>
                <th style={{ width: 64 }}>UOM</th>
                <th style={{ width: 92, textAlign: "right" }}>Unit Price</th>
                <th style={{ width: 104, textAlign: "right" }} title="Price this SKU was booked at on the most recent previous PO (prefers the same customer). Quick discrepancy check.">Prev PO Price</th>
                <th style={{ width: 138 }}>Due Date</th>
                <th style={{ width: 52 }}>Rev</th>
                <th style={{ width: 92, textAlign: "right" }}>Line Total</th>
                <th style={{ width: 34 }}></th>
              </tr>
            </thead>
            <tbody>
              {f.line_items.map((l, i) => {
                const pv = prevFor(l.sku);
                const cp = l.unit_price === "" || l.unit_price == null ? null : Number(l.unit_price);
                const prev = pv && pv.last_price != null ? Number(pv.last_price) : null;
                // Highlight in red when the new price doesn't match the previous PO price for this SKU.
                const mismatch = cp != null && prev != null && Math.abs(cp - prev) > 0.0001;
                return (
                <tr key={i} data-testid={`review-line-${i}`}>
                  <td><ItemSelect value={l.sku} onSelect={(it) => pickItem(i, it)} onText={(t) => setLine(i, "sku", t)} testid={`review-line-sku-${i}`} /></td>
                  <td><input className="jde-input" style={cellInput} value={l.item_number} onChange={(e) => setLine(i, "item_number", e.target.value)} data-testid={`review-line-item-number-${i}`} placeholder="—" /></td>
                  <td><input className="jde-input" style={cellInput} value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} data-testid={`review-line-desc-${i}`} /></td>
                  <td><input className="jde-input" style={{ ...cellInput, textAlign: "right" }} type="number" value={l.quantity_ordered} onChange={(e) => setLine(i, "quantity_ordered", e.target.value)} data-testid={`review-line-qty-${i}`} /></td>
                  <td><input className="jde-input" style={cellInput} value={l.uom} onChange={(e) => setLine(i, "uom", e.target.value)} /></td>
                  <td><input className="jde-input" data-mismatch={mismatch ? "1" : undefined}
                       style={{ ...cellInput, textAlign: "right", ...(mismatch ? { color: "#dc2626", fontWeight: 700, borderColor: "#fca5a5" } : {}) }}
                       type="number" value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} data-testid={`review-line-price-${i}`} /></td>
                  <td className="num text-mono" style={{ textAlign: "right", overflow: "hidden", textOverflow: "ellipsis" }} data-testid={`review-line-prevprice-${i}`}>
                    {!pv || pv.last_price == null ? (
                      <span className="muted" style={{ fontSize: 11 }}>—</span>
                    ) : (
                      <span title={`Last booked on PO ${pv.last_po_number || "—"}${pv.last_date ? ` · ${pv.last_date}` : ""}${!pv.same_customer ? ` · ${pv.last_customer || "other customer"}` : ""}${mismatch ? " — differs from the new price" : ""}`}
                            style={{ color: mismatch ? "#dc2626" : pv.same_customer ? "#334155" : "#b45309", fontWeight: mismatch ? 700 : 400 }}>
                        {fmtCur(pv.last_price, cur)}{pv.same_customer ? "" : " *"}
                      </span>
                    )}
                  </td>
                  <td><input className="jde-input" style={cellInput} type="date" value={l.due_date} onChange={(e) => setLine(i, "due_date", e.target.value)} data-testid={`review-line-due-${i}`} /></td>
                  <td><input className="jde-input" style={cellInput} value={l.rev} onChange={(e) => setLine(i, "rev", e.target.value)} placeholder="—" /></td>
                  <td className="num text-mono" style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{fmtCur(lineTotal(l), cur)}</td>
                  <td style={{ textAlign: "center" }}>
                    <button className="jde-btn jde-btn-sm" onClick={() => removeLine(i)} data-testid={`review-line-remove-${i}`} title="Remove line"
                            style={{ color: "var(--omh-alert)", borderColor: "var(--omh-alert)", padding: "2px 6px" }}>×</button>
                  </td>
                </tr>
                );
              })}
              {f.line_items.length === 0 && (
                <tr><td colSpan={11} className="muted" style={{ textAlign: "center", padding: 16 }}>No line items — click “+ Add line”.</td></tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={9} style={{ textAlign: "right", fontWeight: 700 }}>PO Total</td>
                <td className="num text-mono" style={{ fontWeight: 700 }} data-testid="review-po-total">{fmtCur(poTotal, cur)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
          </div>

          <div style={{ marginTop: 14 }}>
            <label className="jde-label">Notes</label>
            <textarea className="jde-textarea" rows={2} value={f.notes} onChange={(e) => setH("notes", e.target.value)} data-testid="review-notes" />
          </div>

          <AuditTrail poId={po.id} />
        </div>

        <div className="jde-panel-body" style={{ position: "sticky", bottom: 0, background: "#fff", borderTop: "1px solid var(--omh-divider)",
                  display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 10 }}>
          <button className="jde-btn" onClick={onDelete} disabled={saving} data-testid="review-delete"
                  style={{ color: "var(--omh-alert)", borderColor: "var(--omh-alert)" }}>
            Delete
          </button>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            <button className="jde-btn" onClick={onClose} data-testid="review-cancel">Cancel</button>
            <button className="jde-btn" onClick={() => onConfirm(buildPayload())} disabled={saving || !custValid} data-testid="review-confirm"
                    title={!custValid ? "Assign a customer first" : undefined}>
              {saving ? "Working…" : "Confirm PO"}
            </button>
            <button className="jde-btn jde-btn-primary" onClick={() => onConfirmAndPush(buildPayload())} disabled={saving || !custValid} data-testid="review-confirm-push"
                    title={!custValid ? "Assign a customer first" : undefined}>
              {saving ? "Working…" : "Confirm & Push to ERP"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
